import {
  IonBackButton,
  IonBadge,
  IonButtons,
  IonContent,
  IonHeader,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { chevronDownCircleOutline } from "ionicons/icons";
import React, { useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import useNavigation from "../hooks/useNavigation";
import { doReload } from "../hooks/common";
import useDebounce from "../hooks/useDebounce";
import CustomSearchBox from "../components/CustomSearchBox/CustomSearchBox";
import {
  CHANNEL_PARTNERS_PAGE_SIZE,
  getMyInventoryLocations,
  InventoryLocationPartnerType,
} from "../services/inventoryLocation.service";
import { userProfileState } from "../services/state.service";

export type ChannelPartnerSegment = "all" | InventoryLocationPartnerType;

// "My Channel Partners" -- lists the Distributor/Retailer InventoryLocations
// the logged-in salesperson can access, CHANNEL_PARTNERS_PAGE_SIZE at a time
// with infinite scroll -- same skip/disableInfiniteScroll/useDebounce shape
// as PlotList.tsx/FarmList.tsx's own pagination, rather than a bespoke one.
//
// Uses CustomSearchBox (a plain uncontrolled <input>), not IonSearchbar, and
// deliberately doesn't pass a `searchText` prop back into it (same as
// FarmList.tsx) -- IonSearchbar is a Stencil web component whose displayed
// value gets re-synced from its `value` prop on every parent re-render,
// which under frequent re-renders (an in-flight fetch resolving, rapid
// segment taps) could visibly race what's mid-typing. Left fully
// uncontrolled, the DOM owns what's on screen and no re-render can touch it
// -- onChange only ever reports the value out, never writes it back in.
//
// Also deliberately shows no loading spinner over the list on segment/search
// changes (again matching PlotList/FarmList) -- the previous results just
// stay visible until the new page replaces them in one shot, rather than
// flashing to a spinner and back on every keystroke's debounced refetch.
//
// Segment and search are both server-side filters (see
// getMyInventoryLocations) -- changing either restarts pagination from the
// top with the new filter, since re-filtering only the page already loaded
// would miss matches further down the caller's full list. No client-side
// access filter is sent beyond that -- fyllo-core's
// TerritoryInventoryScopeInterceptor already scopes this to the caller's
// territories server-side. Tapping a row navigates to ChannelPartnerDetail
// by id, matching the PlotList/FarmList "{ xId }" route-state convention
// rather than OrderList's "pass the whole object" one, since the detail
// screen needs a fresh full fetch anyway for its Orders tab.
const ChannelPartnerList: React.FC = () => {
  const navigateTo = useNavigation();
  const userProfile: any = useRecoilValue(userProfileState);

  const [locations, setLocations] = useState<any[]>([]);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const [skip, setSkip] = useState(CHANNEL_PARTNERS_PAGE_SIZE);
  const [segment, setSegment] = useState<ChannelPartnerSegment>("all");
  const [searchText, setSearchText] = useState("");
  const debounceSearchText = useDebounce(searchText, 1000);
  // Bumped on every fresh (page-1) fetch; a fetch only applies its result if
  // its token is still current when it resolves. Tapping through segments
  // quickly fires one request per tap -- without this, a slower request for
  // an earlier tap can land after a faster one for the current tap and
  // silently overwrite it, leaving the list showing a segment that's no
  // longer selected.
  const fetchTokenRef = useRef(0);

  useEffect(() => {
    if (!userProfile?.farmUserId) return;
    setSkip(CHANNEL_PARTNERS_PAGE_SIZE);
    setDisableInfiniteScroll(false);
    fetchChannelPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.farmUserId, segment, debounceSearchText]);

  function fetchChannelPartners() {
    const token = ++fetchTokenRef.current;
    const partnerType = segment === "all" ? undefined : segment;
    getMyInventoryLocations({ partnerType, search: debounceSearchText, skip: 0, limit: CHANNEL_PARTNERS_PAGE_SIZE }).then((res) => {
      if (token !== fetchTokenRef.current) return;
      const arr = Array.isArray(res) ? res : [];
      setLocations(arr);
      setDisableInfiniteScroll(arr.length < CHANNEL_PARTNERS_PAGE_SIZE);
    });
  }

  function fetchNext(event: CustomEvent<void>) {
    const token = fetchTokenRef.current;
    const partnerType = segment === "all" ? undefined : segment;
    setSkip(skip + CHANNEL_PARTNERS_PAGE_SIZE);
    getMyInventoryLocations({ partnerType, search: debounceSearchText, skip, limit: CHANNEL_PARTNERS_PAGE_SIZE }).then((res) => {
      if (token === fetchTokenRef.current) {
        const arr = Array.isArray(res) ? res : [];
        if (arr.length > 0) {
          setLocations((prev) => [...prev, ...arr]);
          setDisableInfiniteScroll(arr.length < CHANNEL_PARTNERS_PAGE_SIZE);
        } else {
          setDisableInfiniteScroll(true);
        }
      }
      (event.target as HTMLIonInfiniteScrollElement).complete();
    });
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>My Channel Partners</IonTitle>
        </IonToolbar>
        <CustomSearchBox setSearchText={setSearchText} placeholder="Search channel partners" />
        <IonSegment value={segment} onIonChange={(e) => setSegment((e.detail.value as ChannelPartnerSegment) ?? "all")}>
          <IonSegmentButton value="all">
            <IonLabel>All</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value={InventoryLocationPartnerType.DISTRIBUTOR}>
            <IonLabel>Distributors</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value={InventoryLocationPartnerType.RETAILER}>
            <IonLabel>Retailers</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doReload}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          ></IonRefresherContent>
        </IonRefresher>

        {locations.length === 0 && (
          <IonItem lines="none">
            <IonLabel class="secondary-text">No channel partners found.</IonLabel>
          </IonItem>
        )}

        <IonList>
          {locations.map((loc) => (
            <IonItem button key={loc.id} onClick={() => navigateTo("/tabs/channel-partner-detail", { locationId: loc.id })}>
              <IonLabel>
                <strong>{loc.businessName ?? loc.name}</strong>
                <p>{loc.whatsappNo}</p>
              </IonLabel>
              <IonBadge slot="end" color={loc.partnerType === InventoryLocationPartnerType.DISTRIBUTOR ? "primary" : "medium"}>
                {loc.partnerType === InventoryLocationPartnerType.DISTRIBUTOR ? "Distributor" : "Retailer"}
              </IonBadge>
            </IonItem>
          ))}
        </IonList>

        <IonInfiniteScroll threshold="100px" disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => fetchNext(e)}>
          <IonInfiniteScrollContent loadingText="Loading more..."></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default ChannelPartnerList;
